import {
  type AuthToken,
  type AuthTokenData,
  parseAccessToken,
  shouldRefreshAccessToken,
} from "./token";
import { patchClient } from "@/api/patchClient";
import { AuthError } from "@auth/core/errors";
import {
  orgAccountServiceOrgExchangeToken,
  orgAccountServiceOrgLogin,
  orgAccountServiceRefreshToken,
  type deputy_v1_orgaccount_AuthToken,
} from "@deputy-ai/data-platform-api/v1/orgaccount";
import NextAuth, { type DefaultSession, type User, type JWT } from "next-auth";
import Credentials from "next-auth/providers/credentials";

declare module "next-auth" {
  interface JWT {
    authToken: AuthToken;
  }
  /**
   * The shape of the user object returned in the OAuth providers' `profile` callback,
   * or the second parameter of the `session` callback, when using a database.
   */
  interface User {
    authToken: AuthToken;
  }
  /**
   * The shape of the account object returned in the OAuth providers' `account` callback,
   * Usually contains information about the provider being used, like OAuth tokens (`access_token`, etc).
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface Account {}

  /**
   * Returned by `auth`, `useSession`, `getSession` and received as a prop on the `SessionProvider` React Context
   */
  interface Session {
    authToken: AuthToken;
    data: AuthTokenData;
    user: {
      /**
       * By default, TypeScript merges new interface properties and overwrites existing ones.
       * In this case, the default session user properties will be overwritten,
       * with the new ones defined above. To keep the default session user properties,
       * you need to add them back into the newly declared interface.
       */
    } & DefaultSession["user"];
  }
}

const red = "\x1b[31m";
const yellow = "\x1b[33m";
const grey = "\x1b[90m";
const reset = "\x1b[0m";

const orgLogin = patchClient(orgAccountServiceOrgLogin, false);
const exchangeToken = patchClient(orgAccountServiceOrgExchangeToken, false);
const refreshToken = patchClient(orgAccountServiceRefreshToken, false);

const handleError = (error?: unknown) => {
  if (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(
      (error as any)?.message || "Failed to login, please try again"
    );
  }
};

export const { handlers, signIn, signOut, auth } = NextAuth({
  // jwt: {
  //   maxAge,
  // },
  providers: [
    Credentials({
      // You can specify which fields should be submitted, by adding keys to the `credentials` object.
      // e.g. domain, username, password, 2FA token, etc.
      credentials: {
        orgUid: {},
        username: {},
        password: {},
        tokenSource: {},
        token3p: {},
      },
      authorize: async (credentials) => {
        const { orgUid, username, password, tokenSource, token3p } =
          credentials;
        let authToken: deputy_v1_orgaccount_AuthToken | undefined;

        if (orgUid && tokenSource && token3p) {
          const resp = await exchangeToken({
            body: {
              orgUid: orgUid as string,
              tokenSource: tokenSource as string,
              token3p: token3p as string,
            },
          });
          handleError(resp.error);
          authToken = resp.data?.authToken;
        } else if (orgUid && username && password) {
          const resp = await orgLogin({
            body: {
              orgUid: credentials.orgUid as string,
              username: credentials.username as string,
              password: credentials.password as string,
            },
          });
          handleError(resp.error);
          authToken = resp.data?.authToken;
        }
        if (process.env.NODE_ENV === "development") {
          console.log(`----------------- logined with authToken:
${JSON.stringify(authToken, null, 2)}
-----------------`);
        }
        if (!authToken) {
          throw new Error("Invalid credentials");
        }

        return {
          authToken,
        } satisfies User;
      },
    }),
  ],
  callbacks: {
    async jwt(arg) {
      const { token, user } = arg;

      if (user) {
        return {
          ...token,
          authToken: user.authToken,
        } satisfies JWT;
      }

      const authToken = token.authToken as AuthToken;
      if (authToken.accessToken && authToken.refreshToken) {
        if (shouldRefreshAccessToken(authToken.accessToken)) {
          const resp = await refreshToken({
            body: {
              refreshToken: authToken.refreshToken,
            },
          });
          const newAuthToken = resp.data?.authToken;

          if (!newAuthToken) {
            throw new Error("Invalid token");
          }
          return {
            ...token,
            authToken: newAuthToken,
          } satisfies JWT;
        }
      }

      return token;
    },
    async session(arg) {
      // console.log("auth session", arg);
      const { session, token } = arg;
      const authToken = token.authToken as AuthToken;
      const jwt = parseAccessToken(authToken.accessToken);
      if (!jwt) {
        throw new Error("Invalid token");
      }

      session.authToken = authToken;
      session.data = jwt.data;

      return session;
    },
  },
  pages: {
    signIn: "/login",
  },
  logger: {
    error(error) {
      const name =
        error instanceof AuthError ? (error as any).type : error.name;
      console.error(`${red}[auth][error]${reset} ${name}: ${error.message}`);
      if (
        error.cause &&
        typeof error.cause === "object" &&
        "err" in error.cause &&
        error.cause.err instanceof Error
      ) {
        const { err, ...data } = error.cause;
        console.error(`${red}[auth][cause]${reset}:`, err.stack);
        if (data)
          console.error(
            `${red}[auth][details]${reset}:`,
            JSON.stringify(data, null, 2)
          );
      } else if (error.stack) {
        console.error(error.stack.replace(/.*/, "").substring(1));
      }
    },
    warn(code) {
      if (code === "env-url-basepath-redundant") {
        return;
      }
      const url = `https://warnings.authjs.dev#${code}`;
      console.warn(
        `${yellow}[auth][warn][${code}]${reset}`,
        `Read more: ${url}`
      );
    },
    debug(message, metadata) {
      console.log(
        `${grey}[auth][debug]:${reset} ${message}`,
        JSON.stringify(metadata, null, 2)
      );
    },
  },
  cookies: {
    sessionToken: {
      options: {
        httpOnly: false,
        sameSite: "none",
        secure: true,
      },
    },
  },
});

export async function authSessionData() {
  const session = await auth();
  const data = session?.data;

  if (!data) {
    throw new Error("No data found in session");
  }
  return data;
}

export function validateOrgUid(authData: AuthTokenData, orgUid: string) {
  const { orgUid: authOrgUid } = authData;

  if (authOrgUid !== orgUid) {
    throw new Error("Unauthorized");
  }
}
